import SecondarySalesOrder from "@/services/api/SecondarySalesOrder";
import { handleResponse } from "@/services/axios";
import { saveAsExcelFile } from "@/services/excelService/excelSevice";
import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";

interface LoadingState {
    order: boolean;
    orderItems: boolean;
    export: boolean;
}

interface SaleOrderState {
    secondaryOrderList: any[];
    secondaryOrderItemList: any[];
    loading: LoadingState;
    error: string | null;
}

const initialState: SaleOrderState = {
    secondaryOrderList: [],
    secondaryOrderItemList: [],
    loading: {
        order: false,
        orderItems: false,
        export: false,
    },
    error: null,
};

export const getSecondarySaleList = createAsyncThunk(
    "secondarySaleOrder/getSecondarySaleList",
    async (data: any, { rejectWithValue }) => {
        try {
            const response = await SecondarySalesOrder.getSecondarySaleList(data);
            return handleResponse(response, rejectWithValue);
        } catch (err: any) {
            return rejectWithValue(
                err?.response?.data?.message || "Failed to fetch sales order",
            );
        }
    },
);

export const getSecondarySaleItemList = createAsyncThunk(
    "secondarySaleOrder/getSecondarySaleItemList",
    async (data: any, { rejectWithValue }) => {
        try {
            const response =
                await SecondarySalesOrder.getSecondarySalesItemList(data);
            return handleResponse(response, rejectWithValue);
        } catch (err: any) {
            return rejectWithValue(
                err?.response?.data?.message || "Failed to fetch sales items",
            );
        }
    },
);

export const getSecondarySalesExport = createAsyncThunk(
  "saleOrder/getSecondarySalesExport",
  async (data: any, { rejectWithValue }) => {
    try {
      const response = await SecondarySalesOrder.getSecondarySalesExport(data);
      return saveAsExcelFile(response, "Secondary_SalesOrder");
    } catch (err: any) {
      return rejectWithValue(
        err?.response?.data?.message || "Failed to export",
      );
    }
  },
);

const secondarySalesOrderSlice = createSlice({
    name: "secondarySaleOrder",
    initialState,
    reducers: {},
    extraReducers: (builder) => {
        builder
            .addCase(getSecondarySaleList.pending, (state) => {
                state.loading.order = true;
                state.error = null;
                state.secondaryOrderList = [];
            })
            .addCase(getSecondarySaleList.fulfilled, (state, action) => {
                state.loading.order = false;
                state.secondaryOrderList = action.payload || [];
            })
            .addCase(getSecondarySaleList.rejected, (state, action) => {
                state.loading.order = false;
                state.error = action.payload as string;
            })

            .addCase(getSecondarySaleItemList.pending, (state) => {
                state.loading.orderItems = true;
                state.error = null;
                state.secondaryOrderItemList = [];
            })
            .addCase(getSecondarySaleItemList.fulfilled, (state, action) => {
                state.loading.orderItems = false;
                state.secondaryOrderItemList = action.payload || [];
            })
            .addCase(getSecondarySaleItemList.rejected, (state, action) => {
                state.loading.orderItems = false;
                state.error = action.payload as string;
            })

            .addCase(getSecondarySalesExport.pending, (state) => {
                state.loading.export = true;
                state.error = null;
            })
            .addCase(getSecondarySalesExport.fulfilled, (state, action) => {
                state.loading.export = false;
            })
            .addCase(getSecondarySalesExport.rejected, (state, action) => {
                state.loading.export = false;
                state.error = action.payload as string;
            });
    },
});

export default secondarySalesOrderSlice.reducer;
